/**
 * LoginPage — fuera del AppShell (igual que FormViewerPage).
 *
 * Sprint 16 (cookie httpOnly): el backend responde Set-Cookie + { user } —
 * ya no hay token en el body ni nada que guardar en localStorage.
 *
 * Flujo:
 *   1. login() (lib/auth) → POST /login con credentials:'include'.
 *   2. Si 200: la cookie ya está puesta; cacheamos user en memoria y
 *      redirigimos a la ruta `from` que vino del AuthGuard (deep-link
 *      friendly) o /marketing/dashboard. Si `mustChangePassword=true` gana
 *      siempre /change-password.
 *   3. Si 401: muestra el mensaje genérico que devuelve el backend en el
 *      propio formulario (por eso login() usa fetch directo, no api()).
 *
 * Al montar hacemos un probe /me: si la cookie sigue viva (p.ej. el admin
 * vuelve a /login con sesión abierta), directo a destino sin mostrar el form.
 *
 * Sprint 19 (F5): también acepta `?next=<ruta>` (deep-link validado por
 * safeNext, con prioridad sobre state.from) y `?expired=1` (muestra el aviso
 * «Tu sesión ha caducado, vuelve a entrar.») — ambos los pone lib/api.ts al
 * capturar un 401, o ChangePasswordPage si la sesión caduca al enviar.
 */
import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Lock, AlertCircle, Info } from 'lucide-react'
import { fetchMe, login } from '@/lib/auth'

interface LocationState {
  from?: string
}

function safeNext(value: string | null | undefined): string | null {
  if (!value) return null
  // Anti open-redirect (Sprint 19 — F5): sólo rutas INTERNAS de la SPA, es
  // decir, que empiecen por '/'. Se rechaza explícitamente:
  //   - '//evil.com' (protocol-relative) y cualquier esquema (http://, js:)
  //   - '/login…' (evita loops de redirección sobre esta misma página)
  if (!value.startsWith('/') || value.startsWith('//')) return null
  if (value.startsWith('/login')) return null
  return value
}

export function LoginPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const navigate = useNavigate()
  const location = useLocation()

  // Deep-link + aviso de caducidad (F5): api.ts redirige con
  // /login?expired=1&next=<ruta+search>. El state.from del AuthGuard sigue
  // funcionando como segunda fuente (navegación SPA sin recarga).
  const params = new URLSearchParams(location.search)
  const sessionExpired = params.get('expired') === '1'
  const fromState = (location.state as LocationState | null)?.from
  const nextRoute = safeNext(params.get('next')) ?? safeNext(fromState) ?? '/marketing/dashboard'

  // Probe al montar: si la cookie de sesión sigue viva, no tiene sentido
  // mostrar el form — directo a destino (o /change-password si está marcado).
  // Con cookie httpOnly no hay check síncrono posible; /me es la verdad.
  useEffect(() => {
    let alive = true
    void fetchMe()
      .then((user) => {
        if (!alive || !user) return
        navigate(user.mustChangePassword ? '/change-password' : nextRoute, { replace: true })
      })
      .catch(() => {
        // Red/5xx (contrato F6 de fetchMe): no sabemos si hay sesión —
        // mostramos el formulario con normalidad.
      })
    return () => {
      alive = false
    }
    // Sólo en mount; cambios en location no deben re-disparar el redirect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setErr(null)
    try {
      const result = await login(email, password)
      if (!result.ok) {
        // Limpiamos password al fallar para no facilitar reintentos
        // automáticos del browser y forzar al usuario a re-teclear.
        setPassword('')
        const message = result.message
        // Capitalizamos para presentación más cuidada.
        setErr(message.charAt(0).toUpperCase() + message.slice(1) + (message.endsWith('.') ? '' : '.'))
        return
      }
      if (result.user.mustChangePassword) {
        navigate('/change-password', { replace: true })
      } else {
        navigate(nextRoute, { replace: true })
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="min-h-screen bp-grid flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-6">
          <div className="text-[10px] font-mono uppercase tracking-wider text-blueprint-700">
            JR2 MKT · Admin
          </div>
          {/* h1 para landmark y reading order correcto. Los SR leerán el
              heading antes que el input con autoFocus. */}
          <h1 className="text-ink-900 font-semibold text-lg mt-1">Entrar</h1>
        </div>
        <form onSubmit={submit} className="bp-card p-5 space-y-4" noValidate>
          {sessionExpired && !err && (
            <div
              role="status"
              aria-live="polite"
              className="flex items-start gap-2 text-xs font-mono text-blueprint-700 bg-blueprint-50 border border-blueprint-200 rounded px-2 py-1.5"
            >
              <Info className="size-3.5 mt-0.5 shrink-0" aria-hidden="true" />
              <span>Tu sesión ha caducado, vuelve a entrar.</span>
            </div>
          )}
          <div>
            <label
              htmlFor="login-email"
              className="block text-xs font-mono uppercase tracking-wider text-ink-700/70 mb-1"
            >
              Email
            </label>
            <input
              id="login-email"
              name="email"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
              aria-invalid={Boolean(err)}
              aria-describedby={err ? 'login-error' : undefined}
              className="w-full border border-ink-900/15 rounded px-3 py-2 text-sm bg-white"
            />
          </div>
          <div>
            <label
              htmlFor="login-password"
              className="block text-xs font-mono uppercase tracking-wider text-ink-700/70 mb-1"
            >
              Contraseña
            </label>
            <input
              id="login-password"
              name="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              aria-invalid={Boolean(err)}
              aria-describedby={err ? 'login-error' : undefined}
              className="w-full border border-ink-900/15 rounded px-3 py-2 text-sm bg-white"
            />
          </div>

          {err && (
            <div
              id="login-error"
              role="alert"
              aria-live="assertive"
              className="flex items-start gap-2 text-xs font-mono text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5"
            >
              <AlertCircle className="size-3.5 mt-0.5 shrink-0" aria-hidden="true" />
              <span>{err}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            aria-busy={busy}
            className="bp-btn-primary w-full justify-center"
          >
            <Lock className="size-3.5" aria-hidden="true" />
            {busy ? 'Entrando…' : 'Entrar'}
          </button>
        </form>
      </div>
    </div>
  )
}

export default LoginPage
